import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Row } from 'react-bootstrap';
import AdvanceTableSearchBox from 'components/common/advance-table/AdvanceTableSearchBox';
import AdvanceTableFooter from 'components/common/advance-table/AdvanceTableFooter';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import moment from 'moment';
import IframeContainerNeedRefill from 'components/navbar/top/IframeContainerNeedRefill';
import { formatHoldQty, hasHoldQty } from 'helpers/holdQty';

const columns = [
    {
        accessorKey: 'patient',
        header: 'Patient',
        meta: {
            headerProps: { className: 'text-900 text-start' },
            cellProps: { className: 'text-start' }
        }
    },
    {
        accessorKey: 'dob',
        header: 'DOB',
        meta: {
            headerProps: { className: 'text-900 text-start' },
            cellProps: { className: 'text-start' }
        }
    },
    {
        accessorKey: 'doctor',
        header: 'Doctor',
        meta: {
            headerProps: { className: 'text-900 text-start' },
            cellProps: { className: 'text-start' }
        }
    },
    {
        accessorKey: 'rx',
        header: 'Rx',
        meta: {
            headerProps: { className: 'text-900 text-start' },
            cellProps: { className: 'text-start' }
        }
    },
    {
        accessorKey: 'drug',
        header: 'Drug',
        meta: {
            headerProps: { className: 'text-900 text-start' },
            cellProps: { className: 'text-start' }
        }
    },
    {
        accessorKey: 'reason',
        header: 'Reason',
        meta: {
            headerProps: { className: 'text-900 text-start' },
            cellProps: { className: 'text-start' }
        }
    }
];

const CycleCombinedRefillGrid = ({ patientdetails_arr, cycleid }) => {
    const [rows, setRows] = useState([]);
    const [showIframe, setShowIframe] = useState(false);
    const [iframeKey, setIframeKey] = useState(0);
    const [csvFlag, setcsvFlag] = useState(false);
    const [printLoader, setPrintLoader] = useState(false);
    const [exportLoader, setExportLoader] = useState(false);

    useEffect(() => {
        fetchPatientData();
    }, [patientdetails_arr]);

    const getDoctorName = (patient, prescription) => {
        const prescriptionDoctor = `${prescription?.doctor_firstname || ''} ${prescription?.doctor_lastname || ''}`.trim();
        const patientDoctor = `${patient?.doctor_first || ''} ${patient?.doctor_last || ''}`.trim();

        return prescriptionDoctor || patientDoctor;
    };

    const buildRow = (patient, prescription, reason) => ({
        patient: `${patient?.patient_first || ''} ${patient?.patient_last || ''}`.trim(),
        dob: patient?.patient_dob ? moment(patient.patient_dob, 'YYYY-MM-DD').format('MM/DD/YY') : '',
        doctor: getDoctorName(patient, prescription),
        rx: prescription?.pharmacy_order_id || '',
        drug: prescription?.drug || '',
        reason
    });

    function fetchPatientData() {
        let results = [];
        const holdReasons = JSON.parse(localStorage.getItem("activeHoldReasonsData") || "[]");

        if (!patientdetails_arr) {
            setRows(results);
            return;
        }

        for (let patient in patientdetails_arr) {
            const patientObj = patientdetails_arr[patient];
            const prescriptions = patientObj["prescriptions"] || {};
            const needRefills = prescriptions.need_refill || [];
            const missingRxs = prescriptions.missing_rxs || [];
            const onHolds = (prescriptions.on_hold || []).filter(value => Number(value.is_hold) === 1);

            needRefills.forEach(value => {
                results.push(buildRow(patientObj, value, value.rx_status || 'Need Refill'));
            });

            missingRxs.forEach(value => {
                results.push(buildRow(patientObj, value, 'PA'));
            });

            onHolds.forEach(value => {
                const holdReason = holdReasons.find(reason => Number(reason.value) === Number(value.holdreason_id))?.key || '';
                const holdQty = hasHoldQty(value.qty) ? ` (Qty: ${formatHoldQty(value.qty)})` : '';
                results.push(buildRow(patientObj, value, `Hold- ${holdReason}${holdQty}`));
            });
        }

        setRows(results);
    }

    const table = useAdvanceTable({
        data: rows,
        columns,
        selection: false,
        sortable: true,
        pagination: true
    });

    const handleIframeToggle = () => {
        setIframeKey(iframeKey + 1);
        setShowIframe(true);
    };

    const handlecsvexport = () => {
        setIframeKey(iframeKey + 1);
        setShowIframe(true);
        setcsvFlag(true);
    };

    return (
        <AdvanceTableProvider {...table}>
            <Row className='mt-3 justify-content-between'>
                <Col xs="4" className="mb-3">
                    <AdvanceTableSearchBox placeholder="Search..." />
                </Col>
                <Col xs="2">
                    <Button color="primary" disabled={printLoader} className='w-100' onClick={handleIframeToggle}>
                        Print
                    </Button>
                </Col>
                {Number(localStorage.getItem("roleId")) > 3 && <Col xs="2">
                    <Button color="primary" disabled={exportLoader} className='w-100' onClick={handlecsvexport}>
                        Export CSV
                    </Button>
                </Col>}
            </Row>
            <Row>
                <Col xs="12" className="mb-1 mt-1">
                    {rows.length > 0 ? (
                        <AdvanceTable
                            headerClassName="bg-200 text-nowrap align-middle text-center"
                            rowClassName="text-nowrap align-middle text-center"
                            tableProps={{
                                size: 'sm',
                                striped: true,
                                className: 'fs-10 mb-0 overflow-hidden'
                            }}
                        />
                    ) : (
                        <Alert variant="info" className="mb-0 text-center">
                            No records found.
                        </Alert>
                    )}
                </Col>
            </Row>
            <Card.Footer>
                <div className="mt-3">
                    <AdvanceTableFooter
                        rowInfo
                        navButtons
                        rowsPerPageSelection
                    />
                </div>
            </Card.Footer>
            {showIframe && (
                <IframeContainerNeedRefill
                    key={iframeKey}
                    identifier={1}
                    id={cycleid}
                    csvFlag={csvFlag}
                    setcsvFlag={setcsvFlag}
                    setPrintLoader={setPrintLoader}
                    setExportLoader={setExportLoader}
                />
            )}
        </AdvanceTableProvider>
    );
};

export default CycleCombinedRefillGrid;
 